import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { FiActivity, FiClock, FiMapPin, FiNavigation, FiRefreshCcw, FiTruck } from 'react-icons/fi';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { listarHistoricoMotoboys, listarMotoboysRastreamento } from '../../api/rastreamento';
import { MotoboyHistorico, MotoboyHistoricoPonto, MotoboyLocalizacao } from '../../types';
import { formatarMoeda } from '../../utils/formatadores';

delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const CORES = ['#1845ba', '#1e9e58', '#f59e0b', '#db2777', '#0ea5e9', '#8b5cf6'];

function formatarDataHora(valor: string) {
  return new Date(valor).toLocaleString('pt-BR');
}

function tempoRelativo(valor: string) {
  const diff = Date.now() - new Date(valor).getTime();
  const minutos = Math.max(0, Math.round(diff / 60000));
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `${minutos} min atras`;
  const horas = Math.floor(minutos / 60);
  return `${horas}h ${minutos % 60}min atras`;
}

function coordenadasValidas(latitude: number, longitude: number) {
  return Number.isFinite(latitude) && Number.isFinite(longitude) && Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180;
}

function ordenarPontos(pontos: MotoboyHistoricoPonto[]) {
  return [...pontos].sort((a, b) => new Date(a.coletado_em).getTime() - new Date(b.coletado_em).getTime());
}

function distanciaKm(a: MotoboyHistoricoPonto, b: MotoboyHistoricoPonto) {
  const lat1 = (a.latitude * Math.PI) / 180;
  const lat2 = (b.latitude * Math.PI) / 180;
  const dLat = lat2 - lat1;
  const dLng = ((b.longitude - a.longitude) * Math.PI) / 180;
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLng / 2);
  const h = s1 * s1 + Math.cos(lat1) * Math.cos(lat2) * s2 * s2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function calcularVelocidade(pontos: MotoboyHistoricoPonto[]) {
  const validos = ordenarPontos(pontos.filter((ponto) => coordenadasValidas(ponto.latitude, ponto.longitude)));
  if (validos.length < 2) return null;

  for (let indice = validos.length - 1; indice > 0; indice -= 1) {
    const atual = validos[indice];
    const anterior = validos[indice - 1];
    const deltaMs = new Date(atual.coletado_em).getTime() - new Date(anterior.coletado_em).getTime();
    if (deltaMs <= 0) continue;
    const velocidade = distanciaKm(anterior, atual) / (deltaMs / 3600000);
    if (Number.isFinite(velocidade) && velocidade <= 160) return Math.max(0, velocidade);
  }

  return null;
}

function calcularKm(pontos: MotoboyHistoricoPonto[]) {
  const hoje = new Date();
  const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()).getTime();
  let hojeKm = 0;
  let totalKm = 0;
  const validos = ordenarPontos(pontos.filter((ponto) => coordenadasValidas(ponto.latitude, ponto.longitude)));

  for (let indice = 1; indice < validos.length; indice += 1) {
    const anterior = validos[indice - 1];
    const atual = validos[indice];
    const deltaMs = new Date(atual.coletado_em).getTime() - new Date(anterior.coletado_em).getTime();
    if (deltaMs <= 0) continue;
    const trecho = distanciaKm(anterior, atual);
    const velocidade = trecho / (deltaMs / 3600000);
    if (!Number.isFinite(trecho) || trecho < 0.03 || !Number.isFinite(velocidade) || velocidade > 160) continue;
    totalKm += trecho;
    if (new Date(atual.coletado_em).getTime() >= inicioHoje) hojeKm += trecho;
  }

  return { hojeKm, totalKm };
}

function MapaMotoboys({
  motoboys,
  historicos,
  selecionados,
  mostrarRota,
  mostrarVelocidade,
}: {
  motoboys: MotoboyLocalizacao[];
  historicos: MotoboyHistorico[];
  selecionados: number[];
  mostrarRota: boolean;
  mostrarVelocidade: boolean;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapaRef = useRef<L.Map | null>(null);
  const camadaRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (!ref.current || mapaRef.current) return;
    mapaRef.current = L.map(ref.current, { zoomControl: true, preferCanvas: true }).setView([-23.55, -46.63], 11);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(mapaRef.current);
    camadaRef.current = L.layerGroup().addTo(mapaRef.current);
  }, []);

  useEffect(() => {
    const mapa = mapaRef.current;
    const camada = camadaRef.current;
    if (!mapa || !camada) return;

    camada.clearLayers();
    const historicoPorId = new Map(historicos.map((item) => [item.funcionario_id, item]));
    const bounds = L.latLngBounds([]);

    motoboys
      .filter((item) => selecionados.includes(item.funcionario_id))
      .forEach((motoboy, indice) => {
        if (!coordenadasValidas(motoboy.latitude, motoboy.longitude)) return;

        const cor = CORES[indice % CORES.length];
        const historico = historicoPorId.get(motoboy.funcionario_id);
        const rota = historico?.pontos.filter((ponto) => coordenadasValidas(ponto.latitude, ponto.longitude)) || [];

        if (mostrarRota && rota.length > 1) {
          L.polyline(
            rota.map((ponto) => [ponto.latitude, ponto.longitude] as [number, number]),
            { color: cor, weight: 5, opacity: 0.72 }
          ).addTo(camada);
        }

        const marker = L.marker([motoboy.latitude, motoboy.longitude], { title: motoboy.motoboy_nome }).addTo(camada);
        const velocidade = mostrarVelocidade && historico ? calcularVelocidade(historico.pontos) : null;
        if (mostrarVelocidade && velocidade !== null) {
          marker.bindTooltip(`${velocidade.toFixed(1)} km/h`, { direction: 'top', offset: [0, -20] });
        }

        marker.bindPopup(
          `<strong>${motoboy.motoboy_nome}</strong><br/>` +
          `${motoboy.pedido_id ? `Pedido #${motoboy.pedido_id} - ${motoboy.pedido_identificacao_cliente || 'Cliente'}` : 'Sem pedido vinculado'}<br/>` +
          `Atualizado: ${formatarDataHora(motoboy.coletado_em)}`
        );

        bounds.extend([motoboy.latitude, motoboy.longitude]);
      });

    if (bounds.isValid()) {
      mapa.fitBounds(bounds.pad(0.18));
    }
  }, [historicos, motoboys, mostrarRota, mostrarVelocidade, selecionados]);

  return <div className="lizzi-rastreamento-mapa" ref={ref} />;
}

export function RastreamentoMotoboys() {
  const [motoboys, setMotoboys] = useState<MotoboyLocalizacao[]>([]);
  const [historicos, setHistoricos] = useState<MotoboyHistorico[]>([]);
  const [selecionados, setSelecionados] = useState<number[]>([]);
  const [mostrarRota, setMostrarRota] = useState(true);
  const [mostrarVelocidade, setMostrarVelocidade] = useState(false);
  const [intervaloSegundos, setIntervaloSegundos] = useState(15);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date | null>(null);

  async function carregar() {
    try {
      const ultimas = await listarMotoboysRastreamento();
      setMotoboys(ultimas);
      setSelecionados((atual) => {
        const ids = ultimas.map((item) => item.funcionario_id);
        return atual.length ? atual.filter((id) => ids.includes(id)) : ids;
      });
      if (ultimas.length) {
        const historico = await listarHistoricoMotoboys(ultimas.map((item) => item.funcionario_id));
        setHistoricos(historico);
      } else {
        setHistoricos([]);
      }
      setUltimaAtualizacao(new Date());
      setErro(null);
    } catch (error: any) {
      setErro(error.response?.data?.erro || 'Nao foi possivel carregar o rastreamento.');
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    void carregar();
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void carregar();
    }, Math.max(5, intervaloSegundos) * 1000);

    return () => window.clearInterval(timer);
  }, [intervaloSegundos]);

  const historicoPorId = useMemo(() => new Map(historicos.map((item) => [item.funcionario_id, item])), [historicos]);
  const filtrados = useMemo(
    () => motoboys.filter((item) => selecionados.includes(item.funcionario_id)),
    [motoboys, selecionados]
  );

  const resumo = useMemo(() => {
    let kmHoje = 0;
    let kmTotal = 0;
    let pedidosAtivos = 0;

    filtrados.forEach((motoboy) => {
      if (motoboy.pedido_id) pedidosAtivos += 1;
      const historico = historicoPorId.get(motoboy.funcionario_id);
      if (historico) {
        const km = calcularKm(historico.pontos);
        kmHoje += km.hojeKm;
        kmTotal += km.totalKm;
      }
    });

    return { kmHoje, kmTotal, pedidosAtivos };
  }, [filtrados, historicoPorId]);

  function toggleSelecionado(id: number) {
    setSelecionados((atual) => {
      if (atual.includes(id)) return atual.length > 1 ? atual.filter((item) => item !== id) : atual;
      return [...atual, id];
    });
  }

  return (
    <AppLayout>
      <div className="lizzi-cabecalho-pagina">
        <h1>
          <span className="lizzi-icone-titulo">
            <FiMapPin size={18} />
          </span>
          Rastreamento de motoboys
        </h1>
        <Button type="button" variant="outline" fullWidth={false} onClick={() => void carregar()} disabled={carregando}>
          <FiRefreshCcw size={15} style={{ marginRight: 6 }} />
          {carregando ? 'Atualizando...' : 'Atualizar agora'}
        </Button>
      </div>

      <p style={{ fontSize: '14px', color: 'var(--cor-texto)', opacity: 0.75, marginTop: '-12px', marginBottom: '24px' }}>
        Veja a ultima posicao enviada por cada motoboy, a rota do dia e o pedido que esta em entrega.
      </p>

      {erro && <div className="lizzi-rastreamento-erro">{erro}</div>}

      <div className="lizzi-rastreamento-stats">
        <Card className="lizzi-rastreamento-stat-card">
          <span className="lizzi-rastreamento-stat-icon"><FiTruck size={18} /></span>
          <div>
            <strong>{filtrados.length}</strong>
            <p>Motoboys ativos no mapa</p>
          </div>
        </Card>
        <Card className="lizzi-rastreamento-stat-card">
          <span className="lizzi-rastreamento-stat-icon"><FiNavigation size={18} /></span>
          <div>
            <strong>{resumo.pedidosAtivos}</strong>
            <p>Pedidos vinculados</p>
          </div>
        </Card>
        <Card className="lizzi-rastreamento-stat-card">
          <span className="lizzi-rastreamento-stat-icon"><FiActivity size={18} /></span>
          <div>
            <strong>{resumo.kmHoje.toFixed(1)} km</strong>
            <p>Rodado hoje</p>
          </div>
        </Card>
        <Card className="lizzi-rastreamento-stat-card">
          <span className="lizzi-rastreamento-stat-icon"><FiClock size={18} /></span>
          <div>
            <strong>{ultimaAtualizacao ? ultimaAtualizacao.toLocaleTimeString('pt-BR') : '--:--'}</strong>
            <p>Ultima sincronizacao</p>
          </div>
        </Card>
      </div>

      <Card style={{ marginBottom: '18px' }}>
        <div className="lizzi-rastreamento-toolbar">
          <div>
            <span className="lizzi-rastreamento-label">Motoboys no mapa</span>
            <div className="lizzi-rastreamento-chips">
              {motoboys.map((motoboy, indice) => (
                <button
                  type="button"
                  key={motoboy.funcionario_id}
                  className={`lizzi-rastreamento-chip ${selecionados.includes(motoboy.funcionario_id) ? 'is-active' : ''}`}
                  onClick={() => toggleSelecionado(motoboy.funcionario_id)}
                >
                  <i style={{ background: CORES[indice % CORES.length] }} />
                  <span>{motoboy.motoboy_nome}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="lizzi-rastreamento-controls">
            <label>
              <input type="checkbox" checked={mostrarRota} onChange={(event) => setMostrarRota(event.target.checked)} />
              Exibir rota
            </label>
            <label>
              <input type="checkbox" checked={mostrarVelocidade} onChange={(event) => setMostrarVelocidade(event.target.checked)} />
              Exibir velocidade
            </label>
            <label className="lizzi-rastreamento-label-inline">
              Atualizar a cada
              <select value={intervaloSegundos} onChange={(event) => setIntervaloSegundos(Number(event.target.value))}>
                {[5, 10, 15, 20, 30].map((valor) => (
                  <option key={valor} value={valor}>{valor}s</option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </Card>

      <Card style={{ marginBottom: '18px', padding: 0, overflow: 'hidden' }}>
        {carregando && !motoboys.length ? (
          <div style={{ padding: '24px' }}>Preparando mapa...</div>
        ) : (
          <MapaMotoboys
            motoboys={motoboys}
            historicos={historicos}
            selecionados={selecionados}
            mostrarRota={mostrarRota}
            mostrarVelocidade={mostrarVelocidade}
          />
        )}
      </Card>

      <Card>
        <h2 style={{ marginTop: 0, fontSize: '16px' }}>Ultimas posicoes recebidas</h2>
        <div className="lizzi-rastreamento-table-wrap">
          <table className="lizzi-tabela">
            <thead>
              <tr>
                <th>Motoboy</th>
                <th>Ultima atualizacao</th>
                <th>Precisao</th>
                <th>Km hoje</th>
                <th>Km total</th>
                {mostrarVelocidade && <th>Velocidade</th>}
                <th>Pedido</th>
                <th>Link</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((motoboy) => {
                const historico = historicoPorId.get(motoboy.funcionario_id);
                const km = historico ? calcularKm(historico.pontos) : { hojeKm: 0, totalKm: 0 };
                const velocidade = historico ? calcularVelocidade(historico.pontos) : null;

                return (
                  <tr key={motoboy.funcionario_id}>
                    <td>
                      <strong>{motoboy.motoboy_nome}</strong>
                      <br />
                      <small>{motoboy.motoboy_celular || '-'}</small>
                    </td>
                    <td>
                      {formatarDataHora(motoboy.coletado_em)}
                      <br />
                      <small>{tempoRelativo(motoboy.coletado_em)}</small>
                    </td>
                    <td>{motoboy.precisao_metros ? `${Math.round(motoboy.precisao_metros)} m` : '-'}</td>
                    <td>{km.hojeKm.toFixed(1)} km</td>
                    <td>{km.totalKm.toFixed(1)} km</td>
                    {mostrarVelocidade && <td>{velocidade !== null ? `${velocidade.toFixed(1)} km/h` : 'Sem dados'}</td>}
                    <td>
                      {motoboy.pedido_id
                        ? `#${motoboy.pedido_id} - ${motoboy.pedido_identificacao_cliente || 'Cliente'}`
                        : 'Sem pedido'}
                      {motoboy.pedido_total ? <><br /><small>{formatarMoeda(motoboy.pedido_total)}</small></> : null}
                    </td>
                    <td>
                      {motoboy.link_rastreamento ? (
                        <a href={motoboy.link_rastreamento} target="_blank" rel="noreferrer">abrir painel</a>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                );
              })}

              {!filtrados.length && (
                <tr>
                  <td colSpan={mostrarVelocidade ? 8 : 7} style={{ textAlign: 'center', padding: '18px' }}>
                    Nenhum motoboy com localizacao recebida no momento.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </AppLayout>
  );
}
